// The Rules screen doubles as the glossary: four reference sections (tile
// families, sets, calls, turn flow) matching the header keys T5 scaffolded.
// Each section is a card with a heading + one or more body paragraphs; the copy
// itself lives in the i18n dictionaries so it stays en/ru parity-tested.

import type { MenuScreen } from '../types.js';
import { createScreenShell, createMenuHeader, createCrestButton, el } from '@bn-games/ui-shell';
import { t } from '../../i18n/I18n.js';

const PARAGRAPH_SEP = '\n\n';

// `as const` keeps the keys as string literals so the typed `t` accepts them.
const SECTIONS = [
  { heading: 'rules.tileFamilies', body: 'rules.tileFamiliesBody' },
  { heading: 'rules.sets', body: 'rules.setsBody' },
  { heading: 'rules.calls', body: 'rules.callsBody' },
  { heading: 'rules.turnFlow', body: 'rules.turnFlowBody' },
] as const;

export function createRulesScreen(): MenuScreen {
  let frame: HTMLElement | null = null;

  return {
    mount(root, ctx) {
      const shell = createScreenShell();
      frame = shell.frame;
      const body = shell.body;

      body.appendChild(createMenuHeader({ title: t('rules.title') }));

      const list = el('div', {
        class: 'rules',
        attrs: { role: 'region', 'aria-label': t('rules.title'), tabindex: '0' },
      });

      for (const section of SECTIONS) {
        const paragraphs = t(section.body).split(PARAGRAPH_SEP);
        list.appendChild(
          el('article', { class: 'rule-card' }, [
            el('h3', { class: 'rule-card__heading', text: t(section.heading) }),
            ...paragraphs.map((p) => el('p', { class: 'rule-card__body', text: p })),
          ]),
        );
      }

      body.appendChild(list);

      const back = el('div', { class: 'button-row back-button-row' }, [
        createCrestButton({ label: t('common.back'), small: true, onClick: () => ctx.navigate('menu') }),
      ]);
      body.appendChild(back);

      root.appendChild(frame);
    },
    unmount() {
      frame?.remove();
      frame = null;
    },
  };
}
